import { TaskContext } from "contexts/TaskDetailContext";
import { useCallback, useContext, useEffect, useState } from "react";
import { getQuestions } from "services/models/modelDetails/modelTasks/questions";
import { useLocation, useParams } from "react-router-dom";
import ColourConstants from "helpers/colourConstants";
import HtmlTooltip from "components/Elements/HtmlTooltip";
import ErrorMessageWithErrorIcon from "components/Elements/ErrorMessageWithErrorIcon";
import { ModelContext } from "contexts/ModelDetailContext";

const questionTypeOptions = [
	{ label: "Checkbox", value: "B" },
	{ label: "Checkbox List", value: "C" },
	{ label: "Date", value: "D" },
	{ label: "Dropdown", value: "O" },
	{ label: "Long Text", value: "L" },
	{ label: "Number", value: "N" },
	{ label: "Short Text", value: "S" },
	{ label: "Time", value: "T" },
];

export default function useModelTaskQuestions({
	taskInfo,
	getError,
	isMounted,
}) {
	const [state, MtxDispatch] = useContext(ModelContext);
	const [, CtxDispatch] = useContext(TaskContext);
	const location = useLocation();
	const { id } = useParams();

	// Initiate States
	const [data, setData] = useState([]);
	const [originalList, setOriginalList] = useState([]);
	const [loading, setLoading] = useState({
		fetch: true,
		loader: false,
	});
	// ARRANGING DATA FROM THE API RESPONSE

	// HANDLE ERROR FROM API RESPONSE
	const errorResponse = (result) => {
		if (result.data.detail) getError(result.data.detail);
		else getError("Something went wrong");
	};

	// memoized fxn
	const apiResponse = useCallback((d) => {
		const question = questionTypeOptions.find((a) => a.value === d.type);
		const options = d?.options?.map((a, index) => (
			<span
				key={a?.id}
				style={{
					color: a?.raiseDefect ? ColourConstants.red : "",
				}}
			>
				{d?.options?.length - 1 === index ? a?.name + "" : a?.name + ", "}
			</span>
		));
		const res = {
			...d,
			questionType: question.label,
			compulsory: d.isCompulsory ? "Yes" : "No",
			captureImageDisplay: d.captureImage ? "Yes" : "No",
			additional:
				d.type === "B" ? (
					<span>
						<strong>Checkbox Caption : </strong>&nbsp;{d.checkboxCaption}
					</span>
				) : d.type === "N" ? (
					<span style={{ display: "flex", gap: 10 }}>
						<span>
							<strong>Decimal Places : </strong>
							{d.decimalPlaces}
						</span>
						{d.minValue !== null ? (
							<span>
								<strong>Minimum Value : </strong>
								{d.minValue}
							</span>
						) : (
							""
						)}
						{d.maxValue !== null ? (
							<span>
								<strong>Maximum Value : </strong>
								{d.maxValue}
							</span>
						) : (
							""
						)}
					</span>
				) : d.type === "C" || d.type === "O" ? (
					<>
						<HtmlTooltip title={options}>
							<p className="max-two-line">
								<span>
									{d?.options === null || d?.options?.length === 0 ? (
										<ErrorMessageWithErrorIcon
											message={"No Options Assigned"}
										/>
									) : (
										<>
											<strong>Options : </strong>&nbsp;
											{options}
										</>
									)}
								</span>
							</p>
						</HtmlTooltip>
					</>
				) : (
					""
				),
		};
		return res;
	}, []);

	const fetchTaskQuestion = async () => {
		try {
			let result = await getQuestions(taskInfo.id);
			if (result.status) {
				if (!isMounted.aborted) {
					result = result.data.map((x) => apiResponse(x));
					setData(result);
					setOriginalList(result);
				}
				CtxDispatch({
					type: "TAB_COUNT",
					payload: {
						countTab: "questionCount",
						data: result?.length,
					},
				});
				if (result.length > 0) {
					CtxDispatch({
						type: "SET_QUESTIONS",
						payload: true,
					});
				} else {
					CtxDispatch({
						type: "SET_QUESTIONS",
						payload: false,
					});
				}
			} else {
				errorResponse(result);
			}
		} catch (e) {
			return;
		}
	};

	useEffect(() => {
		const selectedQuestion = document.getElementById(
			`row${location.state?.modelVersionQuestionID}`
		);
		if (
			!loading.fetch &&
			location.state?.modelVersionQuestionID &&
			selectedQuestion
		) {
			selectedQuestion.style.background = "#ffeb3b";
			selectedQuestion.scrollIntoView({ block: "center", behavior: "smooth" });
		}
	}, [loading, location.state]);

	useEffect(() => {
		const initialFetch = async () => {
			setLoading((th) => ({ ...th, fetch: true }));
			await fetchTaskQuestion();
			setLoading((th) => ({ ...th, fetch: false }));
		};
		initialFetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		CtxDispatch,
		MtxDispatch,
		data,
		errorResponse,
		fetchTaskQuestion,
		id,
		loading,
		originalList,
		setData,
		setLoading,
		setOriginalList,
		state,
	};
}
